import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@atlas/ui/web";
import { atlas } from "../lib/sdk";
import {
  MATRICULAS,
  STORAGE_KEY_ID,
  STORAGE_KEY_META,
  readActiveIdMatricula,
} from "../lib/matricula-data";

/** Gate modal: quando a prefeitura da matricula ativa exige senha (proposta
 *  ou averbacao) e o servidor ainda nao definiu a senha de TRANSACAO
 *  (separada da senha de login), abre um modal bloqueante pedindo o cadastro
 *  com politica de senha forte. Cliente pediu 30/07/2026. */
export function SenhaTransacaoGate() {
  // Re-le a matricula ativa quando storage muda (troca de matricula, hydrate).
  const [idAtiva, setIdAtiva] = useState<string | null>(() => readActiveIdMatricula());
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY_ID || e.key === STORAGE_KEY_META) {
        setIdAtiva(readActiveIdMatricula());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Prefeitura da matricula ativa exige senha (proposta OU averbacao)?
  const matAtiva = useMemo(
    () => MATRICULAS.find((m) => m.idMatricula === idAtiva) ?? null,
    [idAtiva],
  );
  const prefeituraExigeSenha = matAtiva?.precisaSenhaTransacao === true;

  // Status da senha de transacao — fonte de verdade e o backend.
  const statusQ = useQuery({
    queryKey: ["servidor", "transaction-password-status"],
    queryFn: () => atlas.servidor.transactionPasswordStatus(),
    // So consulta se tem matricula ativa e ela exige — evita chamar antes de hydratar.
    enabled: prefeituraExigeSenha,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const precisaCadastrar =
    prefeituraExigeSenha && statusQ.data?.hasTransactionPassword === false;

  if (!precisaCadastrar) return null;
  return <ModalCadastroSenhaTransacao prefeituraNome={matAtiva?.prefeitura ?? ""} />;
}

function ModalCadastroSenhaTransacao({ prefeituraNome }: { prefeituraNome: string }) {
  const qc = useQueryClient();
  const [novaSenha, setNovaSenha] = useState("");
  const [confirma, setConfirma] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [mostrar, setMostrar] = useState(false);

  const checks = useMemo(() => passwordChecks(novaSenha), [novaSenha]);
  const strongOk = checks.every((c) => c.ok);
  const confereOk = novaSenha.length > 0 && novaSenha === confirma;

  const mut = useMutation({
    mutationFn: () => atlas.servidor.setTransactionPassword({ novaSenha }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["servidor", "transaction-password-status"] });
      // Rehydrata matriculas pra baixar precisaSenhaTransacao=false.
      const { hydrateMatriculas } = await import("../lib/matricula-data");
      await hydrateMatriculas();
    },
    onError: (err) => setErro((err as Error).message || "Erro ao salvar a senha."),
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10, 22, 40, 0.75)",
        backdropFilter: "blur(6px)",
        display: "grid",
        placeItems: "center",
        zIndex: 400,
        padding: 16,
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="senha-transacao-title"
    >
      <div
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--border-strong)",
          borderRadius: 14,
          padding: 28,
          maxWidth: 520,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          boxShadow: "var(--shadow-lg)",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.08em",
              fontWeight: 700,
              color: "var(--gold-500)",
              textTransform: "uppercase",
            }}
          >
            Ação obrigatória
          </div>
          <h2 id="senha-transacao-title" style={{ margin: "4px 0 0", fontSize: 20 }}>
            Cadastre sua senha de transação
          </h2>
          <p
            style={{
              margin: "8px 0 0",
              color: "var(--text-muted)",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {prefeituraNome ? <>A <b>{prefeituraNome}</b> passou a exigir</> : <>Sua prefeitura passou a exigir</>} uma
            senha específica pra você autorizar propostas e averbações. Ela é <b>diferente da senha
            que você usa pra entrar no perfil</b> e serve como um segundo fator: sem ela, nenhuma
            operação é registrada em seu nome.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={labelStyle}>Nova senha de transação</label>
          <div style={{ position: "relative" }}>
            <input
              type={mostrar ? "text" : "password"}
              value={novaSenha}
              onChange={(e) => {
                setNovaSenha(e.target.value);
                setErro(null);
              }}
              autoComplete="new-password"
              autoFocus
              style={inputStyle}
            />
            <button
              type="button"
              onClick={() => setMostrar((v) => !v)}
              style={togglerStyle}
              aria-label={mostrar ? "Ocultar senha" : "Mostrar senha"}
            >
              {mostrar ? "Ocultar" : "Mostrar"}
            </button>
          </div>
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "6px 0 0",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "4px 12px",
              fontSize: 12,
            }}
          >
            {checks.map((c) => (
              <li
                key={c.label}
                style={{ color: c.ok ? "var(--emerald-500)" : "var(--text-dim)" }}
              >
                {c.ok ? "✓" : "·"} {c.label}
              </li>
            ))}
          </ul>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={labelStyle}>Confirme a nova senha</label>
          <input
            type={mostrar ? "text" : "password"}
            value={confirma}
            onChange={(e) => {
              setConfirma(e.target.value);
              setErro(null);
            }}
            autoComplete="new-password"
            style={inputStyle}
          />
          {confirma.length > 0 && !confereOk ? (
            <span style={{ fontSize: 12, color: "var(--danger-500)" }}>
              As senhas não coincidem.
            </span>
          ) : null}
        </div>

        {erro ? (
          <div
            style={{
              padding: "10px 12px",
              borderRadius: 8,
              background: "color-mix(in srgb, var(--danger-500) 10%, transparent)",
              border: "1px solid var(--danger-500)",
              fontSize: 13,
              color: "var(--danger-500)",
            }}
          >
            {erro}
          </div>
        ) : null}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="button"
            onClick={() => {
              if (!strongOk || !confereOk || mut.isPending) return;
              setErro(null);
              mut.mutate();
            }}
            disabled={!strongOk || !confereOk || mut.isPending}
          >
            {mut.isPending ? "Salvando..." : "Cadastrar senha"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Regras de senha forte — MESMAS validacoes do backend. */
function passwordChecks(pw: string): { label: string; ok: boolean }[] {
  return [
    { label: "8+ caracteres", ok: pw.length >= 8 },
    { label: "Uma letra maiúscula", ok: /[A-Z]/.test(pw) },
    { label: "Uma letra minúscula", ok: /[a-z]/.test(pw) },
    { label: "Um dígito", ok: /\d/.test(pw) },
    { label: "Um caractere especial", ok: /[^A-Za-z0-9]/.test(pw) },
  ];
}

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
  color: "var(--text-dim)",
  textTransform: "uppercase",
};
const inputStyle: React.CSSProperties = {
  padding: "10px 12px",
  paddingRight: 84,
  borderRadius: 8,
  border: "1px solid var(--border-strong)",
  background: "var(--bg-elev-2)",
  color: "var(--text)",
  fontSize: 15,
  fontFamily: "inherit",
  width: "100%",
  boxSizing: "border-box",
};
const togglerStyle: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  right: 8,
  transform: "translateY(-50%)",
  background: "transparent",
  border: 0,
  color: "var(--text-muted)",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  padding: "4px 8px",
};
